<template>
  <div
      class="dropdown  text-sm text-base-content"
      :class="[classList, {
          'dropdown--custom-styles': customStyles,
      }, defaultHeightAndRound ? 'h-7 2xl:h-9 rounded-lg 2xl:rounded-xl' : 'h-10 2xl:h-10 rounded-lg 2xl:rounded-lg']"
      :style="{
          // ...cssVars,
          width: width || 'auto',
          maxWidth: maxWidth || 'unset',
          minWidth: minWidth || 'unset',
          padding: '0'
      }"
    >
    <!-- <label class="dropdown-label">{{ label }}</label> -->

    <multiselect
      v-model="selectedItems"
      :options="options"
      :multiple="multiple"
      :track-by="'id'"
      :label="'label'"
      :placeholder="placeholder"
      :close-on-select="false"
      :custom-label="customLabel"
      :loading="loading" 
      :disabled="isDisabled" 
      @input="emitValue"
      v-bind="{ ...computedConfig, ...$attrs }" 
    >
        <template #caret="{ toggle }">
            <div class="caret caret--rotable right-2 2xl:right-1.5" :class="isDisabled ? 'tw-hidden' : ''" @mousedown.prevent.stop="toggle()">
                <slot name="caret">
                    <dropdownIcon />
                </slot>
            </div>
        </template>
        <template slot="singleLabel" slot-scope="props">
            <div class="custom-label custom-label--singleLabel">
                <slot name="before" />
                <component v-if="props.option.icon && showIcon" :is="props.option.icon" class="custom-label__icon" />
                <span class="custom-label__text">
                    {{ getLabel(props.option) }}
                </span>
                <slot name="after" />
            </div>
        </template>
        <template slot="placeholder">
            <div class="custom-label custom-label--placeholder">
                <slot name="before" />
                <div class="custom-label__text" :class="{ opacity: !menu }">{{ placeholder }}</div>
                <slot name="after" />
            </div>
        </template>
        <template slot="option" slot-scope="props">
            <slot name="option" :option="props.option">
                <div class="custom-label custom-label--option" :title="getLabel(props.option)">
                    <input
                        v-if="multiple && showCheckBox"
                        type="checkbox"
                        :checked="isOptionChecked(props.option)"
                        class="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300 rounded-md pointer-events-none"
                        :disabled="props.option.$isDisabled"
                    />
                    <component v-if="props.option.icon" :is="props.option.icon" class="custom-label__icon" />
                    <span class="custom-label__text" :style="{ color: optionTextColor }">
                        {{ getLabel(props.option) }}
                    </span>
                    <div v-if="props.option.icons?.length" class="flex ml-6 gap-2">
                        <component v-for="icon in props.option.icons" :key="icon" :is="icon" @click.stop="handleIconClick(icon, props)"/>
                    </div>
                </div>
            </slot>
        </template>
        <template slot="tag" slot-scope="props">
            <slot name="before" />
            <div
                v-if="tags"
                class="custom-tag h-5 text-xs 2xl:h-6 2xl:text-sm"
                :class="{
                    'custom-tag--disabled': props.option.$isDisabled,
                }"
            >
                <span class="truncate custom-tag-label">
                    {{ getLabel(props.option) }}
                </span>
                <button
                    v-if="!props.option.$isDisabled"
                    class="custom-tag__icon"
                    :title="$t('components.remove')"
                    type="button"
                    @click.stop.prevent="props.remove(props.option)"
                >
                    <cross-icon />
                </button>
            </div>
            <slot name="after" />
        </template>
        <template v-if="!tags && props.values.length" slot="selection" slot-scope="props">
            <span class="custom-label custom-label--selection">
                <span class="custom-label__text" :class="selectionClass">
                    {{ props.values.map(getLabel).join(', ') }}
                </span>
            </span>
        </template>
        <template v-if="multiple && options && options.length" slot="beforeList">
                <div class="custom-label custom-label--before-list multiselect__before-list flex items-center gap-1 py-2 px-3">
                    <div v-if="beforeLabel || placeholder" class="custom-label__placeholder">
                       {{ $t('components.search_here') }}
                    </div>
                    <div class="ml-auto flex gap-2">
                        <div
                            v-if="calculatedValue?.length !== options?.length"
                            class="font-normal text-base"
                        >
                            {{ $t('components.default') }}
                        </div>
                        
                    </div>
                </div>
            </template>
        <template v-if="loading" slot="noResult">{{ $t('components.loading') }}</template>
        <template #limit>
            <span class="custom-label custom-label--limit">
                <span class="custom-label__text text-base-content">
                    {{ $t('components.and_count_more', { count: countOverLimit }) }}
                </span>
            </span>
        </template>
        <template #noOptions>
            {{ $t('components.multiselect.list_empty') }}
        </template>
        <template #noResults>
            {{ $t('components.multiselect.no_results') }}
        </template>
        <template slot="option" slot-scope="props">
          <div class="option-row">
            <span>{{ getLabel(props.option) }}</span>
            <input
              type="radio"
              name="primary"
              :checked="primaryId === props.option.id"
              :disabled="!Array.isArray(selectedItems) || !selectedItems.find(i => i.id === props.option.id)"
              @click.stop="setPrimary(props.option.id)"
            />
          </div>
        </template>
    </multiselect>
  </div>
</template>

<script>
import Multiselect from 'vue-multiselect'
import dropdownIcon from "@shared/assets/icons/dropdown.svg";
import crossIcon from "@shared/assets/icons/cross.svg";


export default {
  name: 'MultiSelectPrimary',
  components: { 
    Multiselect,
    dropdownIcon,
    crossIcon
   },
  props: {
    value: [Object, Array, String, Number],
    options: {
      type: Array,
      required: true
    },
    modelValue: {
      type: Array,
      default: () => []
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    label: {
      type: String,
      default: 'Select Items'
    },
    placeholder: String,
    config: {
      type: Object,
      default: () => ({}),
    },
    colors: {
      type: Object,
      default: () => ({}),
    },
    tags: {
      type: Boolean,
      default: true,
    },
    selectionClass: {
      type: [String, Array, Object],
      default: '',
    },
    classList: {
      type: [String, Array, Object],
      default: '',
    },
    customStyles: {
      type: Boolean,
      default: false,
    },
    width: String,
    maxWidth: { type: String, default: () => "15rem" },
    minWidth: String,
    menu: {
      type: Boolean,
      default: false,
    },  
    defaultHeightAndRound: {
      type: Boolean,
      default: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    field: {
      type: Object,
      default: () => {},
    },
    calculatedValue: {
      type: Array,
      default: () => [],
    },
    beforeLabel: {
      type: String,
      default: '',
    },
  },
  emits: ['update:modelValue'],
  data() {
    return {
      selectedItems: [],
      primaryId: null
    }
  },
  created() {
    console.log("this.modelValue ", this.value)
    this.selectedItems = this.modelValue.map(({ id }) =>
      this.options.find(o => o.id === id)
    ).filter(Boolean)

    this.primaryId = this.modelValue.find(i => i.isdefault)?.id || null
  },
  mounted() {
      this.$nextTick(() => {
          this.placeDropdown();
          this.selectedItems = this.value;
          if(!this.field?.is_custom && this.field!=undefined && this.field!=null && this.field?.is_restricted){
              this.defaultselectedItems = {...this.selectedItems};
          }
          if (Array.isArray(this.selectedItems)) {
            const defaultItem = this.selectedItems.find(item => item.system_default === true);
            if (defaultItem) {
              this.primaryId = defaultItem.id;
            }
          }
          // fixes required rule validation is case user removes values from closed dropdown
          if (this.selectedItems?.length) this.$emit('closed');
      });
    },
  computed: {
    computedConfig() {
      return {
          searchable: false,
          limit: 2,
          selectLabel: "",
          deselectLabel: "",
          selectedLabel: null,
          tagPlaceholder: this.$t('components.multiselect.create_tag_placeholder'),
          ...this.config,
      };
    },
    optionTextColor() {
      return this.colors.optionText || 'black';
    },
    isDisabled() {
      return this.disabled;
    },
    countOverLimit() {
      return this.computedConfig.limit
          ? Math.max(0, this.selectedItems.length - this.computedConfig.limit)
          : 0
    },
  },
  methods: {
    emitValue() {
      const value = this.selectedItems.map(item => ({
        id: item.id,
        ...(item.id === this.primaryId ? { is_default: true } : {})
      }))
      // this.$emit('update:modelValue', value)
      this.$emit('input', value)
},
    setPrimary(id) {
      this.primaryId = id
      this.emitValue()
    },
    customLabel(option) {
      return option.label
    },
    getLabel(prop) {
        if (prop?.$groupLabel) {
            return prop.$groupLabel;
        }
        return prop[this.config.label || this.$t('components.title')] || prop;
    },
        placeDropdown() {
          if (this.reposition && this.isOpen) {
              this.$nextTick(() => {
                  const { top, bottom, width, left } = this.$el.getBoundingClientRect();
                  const ref = this.$refs.multiselect;
                  if (ref) {
                      const searchInput = ref.$el.querySelector(".multiselect__input");
                      const listEl = ref.$refs.list;
                      listEl.style.width = `${width}px`;
                      // listEl.style.width = `fit-content`;
                      listEl.style.minWidth = `${width}px`;
                      // listEl.style.position = "fixed";
                      // override 'important' style:
                      listEl.style.cssText += "position: fixed !important;";
                      const listHeight = listEl.getBoundingClientRect().height;
                      const addOffset = this.isSearchOffset ? 36 : 0;
                      if (searchInput) {
                          if (addOffset) {
                              searchInput.style.width = "100%";
                              searchInput.classList.add("h-9");
                              searchInput.setAttribute("placeholder", this.placeholder);
                          } else {
                              searchInput.classList.remove("h-9");
                          }
                      }
                      listEl.style.left = `${left}px`;
                      listEl.style.top = 'auto';
                      if (ref.$el.classList.contains('multiselect--above')) {
                          listEl.style.top = `${top - listHeight}px`;
                      } else {
                          listEl.style.top = `${bottom + addOffset}px`;
                      }
                  }
              });
            }
        },
        isOptionChecked(option) {
            this.placeDropdown();
            if(!this.isTableLargeDropdown) {
                const trackBy = this.computedConfig?.trackBy || 'id';
                return (this.multiple && this.showCheckBox) && this.selectedItems?.some((el) => el[trackBy] === option[trackBy]) || false;
            }
            else {
                const trackBy = this.computedConfig?.trackBy || 'name';
                return this.selectedOptions?.some((el) => el[trackBy] === option[trackBy])||false;
            }
        },
  }
}
</script>

<style lang="scss" scoped>
@import "@shared/assets/mixins/mixins.scss";
@import "@/assets/styles/functions.scss";

.dropdown-label {
  display: block;
  font-weight: bold;
  margin-bottom: 6px;
}

.option-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.dropdown {
    position: relative;
    display: flex;
    align-items: stretch;
    min-width: 114px;
    min-height: 50px;
    padding: 0;
    cursor: pointer;
    &:not(&--custom-styles) {
        @include buttonBase($responsive: true, $customColors: true);
    }
    &.cursor-auto {
        cursor: auto!important;
    }
  }
  
/* 🔥 Remove highlight background & text color on hover/keyboard */
::v-deep(.multiselect__option--highlight) {
  background-color: transparent !important;
  color: inherit !important;
}

/* 🧼 Remove keyboard navigation "Press enter to select" hint */
::v-deep(.multiselect__option--highlight::after) {
  content: none !important;
  display: none !important;
}

/* Custom selected tags (in input area) */
::v-deep(.multiselect__tags) {
  background: none;
  min-height: 24px;
  width: 100%;
  box-sizing: border-box;
  gap: 12px;
  padding: 7px 32px 0 14px;
  border-radius: 0.75rem;
}


/* 💡 OPTIONAL: If you want hover styles for selected tags */
::v-deep(.multiselect__option--selected) {
  background-color: #e6f0ff !important;
  content: none !important;
}

::v-deep(.multiselect__option:hover) {
  background-color: #f0f8ff !important;
   content: none !important;
}

::v-deep(.multiselect__tag){
      background-color: #d9d9d9 !important;
    --tw-text-opacity: 1!important ;
    color: rgba(31, 41, 55, var(--tw-text-opacity));
    font-weight: 600;
}
::v-deep(.multiselect__option--selected::after),
::v-deep(.multiselect__option span[class*="selected"]) {
  display: none !important;
}
::v-deep(.multiselect .multiselect__content-wrapper){
  font-weight: 400;
}
.caret {
        display: inline-flex;
        position: absolute;
        transform: translateY(-50%);
        top: 50%;
        // right: 14px;
        padding: 3px 1px;
        z-index: 1;
        &.tw-hidden {
            display: none;
        }
        svg {
            // width: 12px;
            transition: transform 0.2s;
            @apply fill-current;
            // fill: $caret;
            // path {
            //   fill: $caret;
            // }
        }
    }
    
  .custom-label {
    &--limit & {
        &__text {
            text-wrap: nowrap;
            font-weight: 600;
        }
    }
}
::v-deep(.multiselect__tags .multiselect__tags-wrap) {
  display: inline-flex;
  gap: 0.75rem;
}

.multiselect__tags-wrap .custom-tag
{
  padding: 0 28px 0 12px;
    border-radius: 8px;
    display: inline-flex;
    white-space: nowrap;
    align-items: center;
    font-weight: 600;
    position: relative;
    background-color: #d9d9d9;
    overflow: hidden;
}
.custom-tag__icon {
    position: absolute;
    right: 7px;
    transform: translateY(-50%);
    top: 50%;
    cursor: pointer;
}

.multiselect__option {
        white-space: nowrap;
        overflow: hidden;
        display: flex;
        align-items: center;
        padding: 0;
        color: #282828;
        // custom label template:
        .custom-label {
            display: flex;
            align-items: center;
            gap: 10px;
            width: 100%;
                svg {
                    flex-shrink: 0;
                    @apply w-5 h-5 fill-current;
                }
                &__text {
                    overflow: hidden;
                    text-overflow: ellipsis;
                    flex-grow: 1;
                    text-align: left;
                    &.opacity {
                        opacity: 0.8;
                    }
                }
            }
        }
        .multiselect__single,
        .multiselect__option {
            .custom-label {
                &__text {
                    color: black;
                }
            }
        }
        .multiselect__single,
        .multiselect__placeholder {
            .custom-label {
                svg {
                    @apply fill-current text-primary;
                }
            }
        }
        .multiselect__select {
            z-index: 1;
        }
        .multiselect__element {
            &:not(:first-child) {
                .multiselect__option {
                    border-top: 1px solid #a8a8a8;
                }
            }
        }
        .multiselect__option {
            font-size: toRem(14px);
            min-height: toRem(18px);
            box-sizing: border-box;
            padding: toRem(12px) toRem(20px) toRem(12px) toRem(16px);
            color: #282828;
            font-weight: 500;
            // .custom-label {
            //   svg {
            //       fill: #282828;
            //       path {
            //           fill: #282828;
            //       }
            //   }
            // }
            &--selected {
                background-color: #cce3ff96 !important;
            }
            &--highlight {
                background-color: #cce3ff !important;
                padding-right: toRem(16px);
                border-right-width: 4px;
                border-right-style: solid;
                @apply border-primary-600;
            }
        }
        .multiselect__placeholder {
            width: 100%;
            margin: 0;
            // color: $text;
            // @apply text-base-content;
        }
        .multiselect__input {
            margin-bottom: 0;
            padding: 0;
        }
        .multiselect__spinner {
                position: absolute;
                right: 20px;
                top: 0px;
                width: 48px;
                height: 35px;
                background: #fff0;
                display: block;
            &::before,
            &::after {
                border-color: #0d69d5 transparent transparent;
            }
        }
</style>

